import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, CalendarCheck } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useI18n } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import { api, waLink } from "@/lib/api";

export const AppointmentForm = () => {
  const { t, L } = useI18n();
  const { data } = useSite();
  const s = data?.settings || {};
  const services = data?.services || [];

  const [form, setForm] = useState({ name: "", phone: "", service: "", date: "", time: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/submissions", form);
      setSent(true);
      toast.success(t("form.success"));
    } catch (err) {
      toast.error(t("form.error"));
    } finally {
      setSending(false);
    }
  };

  const waMsg = `Hola Blackbeard Barbería, soy ${form.name || "..."}. Quiero agendar ${
    form.service || "una cita"
  }${form.date ? ` el ${form.date}` : ""}${form.time ? ` a las ${form.time}` : ""}.`;

  const inputCls =
    "w-full bg-panel border border-white/10 rounded-lg px-4 py-3 text-sm text-bone placeholder:text-mut/50 outline-none focus:border-gold/60 transition-colors";

  return (
    <section id="cita" data-testid="appointment-section" className="py-24 sm:py-32 bg-panel/40 border-t border-white/5">
      <Toaster richColors position="bottom-center" theme="dark" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-start">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.6 }}
        >
          <p className="font-mono text-[11px] tracking-[0.3em] text-gold mb-4">{t("form.eyebrow")}</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-bone leading-[0.95]">
            {t("form.title")}
          </h2>
          <p className="mt-5 text-mut max-w-md">{t("form.sub")}</p>
          <div className="mt-10 hidden lg:block">
            <div className="gold-rule w-2/3 mb-10" />
            <a
              data-testid="form-whatsapp-direct-button"
              href={waLink(s.whatsapp)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 bg-whatsapp text-ink font-semibold px-7 py-3.5 rounded-full hover:brightness-110 transition-all hover:scale-[1.02]"
            >
              <MessageCircle size={17} />
              {t("hero.cta_whatsapp")}
            </a>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          onSubmit={submit}
          className="border border-gold/10 bg-ink p-7 sm:p-9 flex flex-col gap-4"
          data-testid="appointment-form"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-gold/80 mb-2">
                {t("form.name")} *
              </label>
              <input
                data-testid="appointment-name-input"
                required
                value={form.name}
                onChange={set("name")}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-gold/80 mb-2">
                {t("form.phone")} *
              </label>
              <input
                data-testid="appointment-phone-input"
                required
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-gold/80 mb-2">
              {t("form.service")}
            </label>
            <select data-testid="appointment-service-select" value={form.service} onChange={set("service")} className={inputCls}>
              <option value="">{t("form.service_any")}</option>
              {services.map((sv) => (
                <option key={sv.id} value={L(sv.name_es, sv.name_en)}>
                  {L(sv.name_es, sv.name_en)}
                </option>
              ))}
            </select>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-gold/80 mb-2">
                {t("form.date")}
              </label>
              <input
                data-testid="appointment-date-input"
                type="date"
                min={new Date().toISOString().split("T")[0]}
                value={form.date}
                onChange={set("date")}
                className={`${inputCls} [color-scheme:dark]`}
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-gold/80 mb-2">
                {t("form.time")}
              </label>
              <input
                data-testid="appointment-time-input"
                type="time"
                value={form.time}
                onChange={set("time")}
                className={`${inputCls} [color-scheme:dark]`}
              />
            </div>
          </div>

          <div>
            <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-gold/80 mb-2">
              {t("form.notes")}
            </label>
            <textarea
              data-testid="appointment-notes-input"
              rows={3}
              placeholder={t("form.notes_ph")}
              value={form.message}
              onChange={set("message")}
              className={`${inputCls} resize-none`}
            />
          </div>

          <button
            data-testid="appointment-form-submit"
            type="submit"
            disabled={sending}
            className="mt-2 inline-flex items-center justify-center gap-2.5 bg-gold text-ink font-semibold px-7 py-3.5 rounded-full hover:bg-goldlight transition-colors disabled:opacity-50"
          >
            <CalendarCheck size={17} />
            {sending ? t("form.sending") : t("form.submit")}
          </button>

          {sent && (
            <a
              data-testid="appointment-whatsapp-confirm-button"
              href={waLink(s.whatsapp, waMsg)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2.5 bg-whatsapp text-ink font-semibold px-7 py-3.5 rounded-full hover:brightness-110 transition-colors"
            >
              <MessageCircle size={17} />
              {t("form.wa_confirm")}
            </a>
          )}
        </motion.form>
      </div>
    </section>
  );
};

export default AppointmentForm;
